import React from 'react';
import { CheckPicker, Checkbox, Button } from 'rsuite';
// import { shouldDisplay } from 'rsuite/Picker';
import propTypes from 'prop-types';
import classNames from 'classnames';

const NONE_CHECKED = 1;
const ALL_CHECKED = 2;
const INDETERMINATE = 3;

// Equivalent of the old `shouldDisplay`
const shouldDisplay = (label, keyword) => {
  if (typeof label !== 'string' || typeof keyword !== 'string') return false;
  return label.toLowerCase().includes(keyword.trim().toLowerCase());
};

const searchFn = (keyword, label) => {
  return shouldDisplay(label, keyword);
};

const getSelectAllState = (checked, displayed) => {
  if (!displayed.some(item => checked.includes(item))) {
    return NONE_CHECKED;
  }
  if (displayed.every(item => checked.includes(item))) {
    return ALL_CHECKED;
  }
  return INDETERMINATE;
};

export const UiMultiSelect = ({
  options,
  onChange,
  requireOneSelected,
  value: initValue,
  ...props
}) => {
  const picker = React.useRef();
  const [checkedValues, setCheckedValues] = React.useState(initValue || []);
  const [displayValues, setDisplayValues] = React.useState(initValue || []);
  const [open, setOpen] = React.useState(undefined);
  const [search, setSearch] = React.useState('');
  const [hasUserInteracted, setHasUserInteracted] = React.useState(false);
  
  // Only show error after user has interacted, not on initial load
  const error = requireOneSelected && hasUserInteracted && !checkedValues.length;

  const selectAllState = React.useMemo(
    () => getSelectAllState(checkedValues, displayValues),
    [checkedValues, displayValues]
  );

  // Sync internal state when the value prop changes from parent (before user interaction)
  React.useEffect(() => {
    if (!hasUserInteracted && initValue && initValue.length > 0) {
      setCheckedValues(initValue);
      setDisplayValues(initValue);
    }
  }, [initValue, hasUserInteracted]);

  React.useEffect(() => {
    setOpen(error || undefined);
  }, [error]);

  const handleOpen = () => {
    setSearch('');
    // Don't clear values on open - keep the current selection visible
  };

  const handleClose = () => {
    setHasUserInteracted(true);
    onChange(checkedValues);
  };

  const handleChange = value => {
    setCheckedValues(value);
    setHasUserInteracted(true);
  };

  const getFilteredValues = search => {
    return options
      .filter(item => searchFn(search, item.label, item))
      .map(item => item.value);
  };

  const handleSearch = newSearch => {
    setSearch(newSearch);
    setDisplayValues(getFilteredValues(newSearch));
  };

  const selectAllFiltered = () => {
    const filteredValues = getFilteredValues(search);
    const newSelected = options
      .filter(
        item =>
          checkedValues.includes(item.value) ||
          filteredValues.includes(item.value)
      )
      .map(item => item.value);
    setCheckedValues(newSelected);
  };

  const unselectAllFiltered = () => {
    const filteredValues = getFilteredValues(search);
    setCheckedValues(checkedValues.filter(v => !filteredValues.includes(v)));
  };

  const handleSelectAll = (_value, checked) => {
    if (checked) {
      selectAllFiltered();
    } else {
      unselectAllFiltered();
    }
  };

  // Extract placeholder from props
  const { placeholder: propPlaceholder, ...restProps } = props;

  const renderValue = (values, items) => {
    if (!values || values.length === 0) {
      return propPlaceholder || 'Select...';
    }
    if (values.length === 1) {
      return items?.[0]?.label || propPlaceholder || 'Select...';
    }
    return `${values.length} accounts selected`;
  };
  
  return (
    <CheckPicker
      ref={picker}
      open={open}
      data={options}
      value={checkedValues}
      onChange={handleChange}
      onSearch={handleSearch}
      onOpen={handleOpen}
      onClose={handleClose}
      cleanable={false}
      searchBy={searchFn}
      className={classNames('ui-multi-select', { error })}
      menuClassName="ui-multi-select-menu"
      {...restProps}
      renderValue={renderValue}
      renderExtraFooter={() => (
        <div className="ui-multi-select-footer">
          <Checkbox
            indeterminate={selectAllState === INDETERMINATE}
            checked={selectAllState === ALL_CHECKED}
            onChange={handleSelectAll}
          >
            Select all {search ? '(filtered)' : ''}
          </Checkbox>
          <Button
            appearance="primary"
            size="sm"
            onClick={() => picker.current?.close()}
          >
            Ok
          </Button>
        </div>
      )}
    />
  );
};

UiMultiSelect.propTypes = {
  options: propTypes.arrayOf(propTypes.object).isRequired,
  onChange: propTypes.func.isRequired,
  value: propTypes.arrayOf(propTypes.any),
  requireOneSelected: propTypes.bool,
};

UiMultiSelect.defaultProps = {
  value: [],
  requireOneSelected: false,
};
